/**
 * The single shared TanStack Query client. Imported by the provider
 * and by the router context (so loaders can prefetch/read the cache).
 */
import { MutationCache, QueryClient } from '@tanstack/react-query'
import { QUERY_DEFAULTS } from '@/config/app.config'
import { ApiError, isCaseCancelledError } from '@/lib/api'

/** HTTP statuses that should never be retried. */
const NO_RETRY_STATUSES = new Set([400, 401, 403, 404, 422])

/*
 * Roots of `caseKeys` / `verificationKeys`. Spelled out rather than imported so
 * this app-level module does not pull the feature barrels (and their cycles) in.
 */
const CASES_ROOT = ['cases'] as const
const VERIFICATIONS_ROOT = ['verifications'] as const

export const queryClient: QueryClient = new QueryClient({
  /*
   * AMLIQ-568: a `409 CASE_CANCELLED` means this tab is stale — someone else
   * cancelled the case. Whatever screen sent the mutation, refetch case and
   * check data so the case re-renders locked (banner, hidden actions). Each
   * call site still shows its own message and closes its own drawer/modal.
   */
  mutationCache: new MutationCache({
    onError: (error) => {
      if (!isCaseCancelledError(error)) return
      void queryClient.invalidateQueries({ queryKey: CASES_ROOT })
      void queryClient.invalidateQueries({ queryKey: VERIFICATIONS_ROOT })
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: QUERY_DEFAULTS.staleTime,
      gcTime: QUERY_DEFAULTS.gcTime,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && NO_RETRY_STATUSES.has(error.status)) {
          return false
        }
        return failureCount < QUERY_DEFAULTS.retry
      },
    },
    mutations: {
      retry: 0,
    },
  },
})
